import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { Topic } from '@shapeable/gesda-types';
import { DownloadAltIconGlyph, ExternalLinkIconGlyph, QuoteIconGlyph, ShareIconGlyph } from '@shapeable/icons';
import { ComponentPropsProvider, ShareMenu, SiteHeaderUtilityButton, useClickOutside, useCopyToClipboard, useLang, useLink } from '@shapeable/ui';
import { formatTopicCitation } from '../../utils/citation';
const cls = classNames('topic-action-bar');

// -------- Types -------->

// const CREATIVE_COMMONS_BY_SA_URL = 'https://creativecommons.org/licenses/by-sa/4.0/';
const CREATIVE_COMMONS_BY_4_0_URL = 'https://creativecommons.org/licenses/by/4.0/deed.en';

// -------- Props -------->

export type TopicActionBarProps = Classable & HasChildren & {
  entity?: Topic;
};

export const TopicActionBarDefaultProps: Omit<TopicActionBarProps, 'entity'> = {
};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(3)};
    padding: ${theme.UNIT(3)};
    border-top: 1px solid ${theme.COLOR('line-subtle')};
    border-bottom: 1px solid ${theme.COLOR('line-subtle')};
  `,
  tablet: css`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: ${theme.UNIT(3)};
    gap: ${theme.UNIT(4)};
  `,
  desktopDevice: css`
    padding: ${theme.UNIT(3)} 0;
  `,
});

const LicenseStyles = breakpoints({
  base: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${theme.UNIT(1)};
    font-weight: 300;
    color: ${theme.COLOR('text')};
    font-size: 1em;;
  `,
});

const LicenseLinkStyles = breakpoints({
  base: css`
    display: inline-flex;
    align-items: center;
    gap: ${theme.UNIT(1)};
    text-decoration: underline;
    text-underline-offset: 0.04em;
    text-underline-position: from-font;
    text-decoration-thickness: 0.8px;
    ${theme.FILL('subtle')};
    color: ${theme.COLOR('subtle')};
    ${theme.TRANSITION('color', 'fill')};
    font-weight: 300;

    &:hover {
      color: ${theme.COLOR('primary')};
      ${theme.FILL('primary')};
    }
  `,
});

const ActionsStyles = breakpoints({
  base: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${theme.UNIT(2)};
  `,
});

const ActionButtonStyles = breakpoints({
  base: css`
    
  `,
});

const ShareMenuWrapStyles = breakpoints({
  base: css`
    position: relative;

    .shp--site-header-utility-button__menu {
      padding-bottom: 0;
      top: -12px;
      left: -8px;
      right: auto;

      .shp--site-header-utility-button__button {
        transform: translateY(-5px) !important;
      }
    }
  `,
  tablet: css`
    .shp--site-header-utility-button__menu {
      left: auto;
      right: 0;
    }
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div`${ContainerStyles}`,
    License: styled.div`${LicenseStyles}`,
      LicenseLink: styled.span`${LicenseLinkStyles}`,
    Actions: styled.div`${ActionsStyles}`,
      ShareMenuWrap: styled.div`${ShareMenuWrapStyles}`,
      ActionButton: styled(SiteHeaderUtilityButton)`${ActionButtonStyles}`,
};

export const TopicActionBar: Shapeable.FC<TopicActionBarProps> = (props) => {
  const { className, children, entity } = props;
  const t = useLang();
  const { Link } = useLink();
  const [isShareMenuVisible, setIsShareMenuVisible] = useState(false);
  const [showCopied, setShowCopied] = useState(false);

  const shareMenuRef = useClickOutside(() => {
    if (isShareMenuVisible) {
      setIsShareMenuVisible(false);
    }
  }, [isShareMenuVisible]);

  const citation = formatTopicCitation(entity);
  const [, copyCitation] = useCopyToClipboard(citation);

  const onShareClick: React.MouseEventHandler = (event) => {
    event.stopPropagation();
    setIsShareMenuVisible((isVisible) => !isVisible);
  };

  const onShareItemClick: React.MouseEventHandler = () => {
    setIsShareMenuVisible(false);
  };

  const onCiteClick: React.MouseEventHandler = (event) => {
    event.stopPropagation();
    if (!citation) {
      return;
    }

    copyCitation();
    setShowCopied(true);
    window.setTimeout(() => setShowCopied(false), 2000);
  };

  const onPdfClick: React.MouseEventHandler = (event) => {
    event.stopPropagation();

    if (!entity?.slug) {
      return;
    }

    const fileName = `${entity.slug}.pdf`;
    const link = document.createElement('a');
    link.href = `/pdf/topics/${fileName}`;
    link.download = fileName;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <ComponentPropsProvider value={{
      MenuItem: {
        colors: {
          background: 'line-very-subtle',
          hover: {
            text: 'canvas',
            fill: 'canvas',
            bold: 'canvas'
          }
        }
      }
    }} >
    <My.Container className={cls.name(className)}>
      <My.License>
        <span>{t('Under a Creative Commons')}</span>
        <Link href={CREATIVE_COMMONS_BY_4_0_URL} target="_blank" rel="noopener noreferrer" >
          <My.LicenseLink>
            {t('CC BY 4.0 license')}
            <ExternalLinkIconGlyph size={14} />
          </My.LicenseLink>
        </Link>
      </My.License>

      <My.Actions>
        <My.ActionButton
          iconComponent={QuoteIconGlyph}
          isInverted={false}
          menuThemeMode="light"
          onClick={onCiteClick}
        >
          {showCopied ? t('Copied') : t('Cite')}
        </My.ActionButton>

        <My.ShareMenuWrap ref={shareMenuRef}>
          <My.ActionButton
            iconComponent={ShareIconGlyph}
            isInverted={false}
            menuThemeMode="light"
            menuAnimation
            isMenuVisible={isShareMenuVisible}
            onClick={onShareClick}
            menu={
              <ShareMenu
                showTitle={false}
                items={['copy-page', 'facebook', 'twitter', 'linkedin', 'email']}
                onItemClick={onShareItemClick}
              />
            }
          >
            {t('Share')}
          </My.ActionButton>
        </My.ShareMenuWrap>

        <My.ActionButton
          iconComponent={DownloadAltIconGlyph}
          isInverted={false}
          menuThemeMode="light"
          onClick={onPdfClick}
        >
          {t('PDF')}
        </My.ActionButton>
      </My.Actions>

      {children}
    </My.Container>
    </ComponentPropsProvider>
  );
};

TopicActionBar.defaultProps = TopicActionBarDefaultProps;
TopicActionBar.cls = cls;
